import type { FileManagerRef } from 'devextreme-react/file-manager';
import type { RefObject } from 'react';
import { apiFetch, readErrorMessage } from '../api/client';
import type { DocumentPreviewState } from '../documentEditor/DocumentPreviewPopup';
import type { DocumentEditorKind } from '../documentEditor/documentFileTypes';
import { showToast } from '../notifications/toast';
import { mountApiBase } from './fileBrowserApi';
import { parseItemPathForRoutes, toVirtualPath, type MountRoute } from './createFileBrowserProvider';
import { focusZipArchiveAfterCreate } from './fileBrowserZip';
import {
  openDocumentEditorSession,
  openEditorBySurface,
  type NavigateFn
} from '../editorShell/editorOpenHelpers';
import {
  buildNewFileMenuItemsFromRegistry,
  getNewFileStarter,
  type NewFileStarter
} from './fileTypeRegistry';

export const JARVIS_NEW_FILE = 'jarvisNewFile';

export type NewOfficeFileKind = 'xlsx' | 'docx';

export type NewFileStarterId = (typeof import('./fileTypeRegistry').NEW_FILE_STARTERS)[number]['id'];

export type NewOfficeFileMeta = {
  extension: string;
  defaultName: string;
  mimeType: string;
  editorKind: DocumentEditorKind;
  label: string;
};

type FileManagerInstance = {
  getCurrentDirectory: () => { path?: string } | null;
  option: (name: string, value: unknown) => void;
  refresh: () => Promise<unknown>;
};

/** Minimal blank OpenXML templates (non-empty — upload API rejects 0-byte files). */
const BLANK_XLSX_BASE64 =
  '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';

const BLANK_DOCX_BASE64 =
  'UEsDBBQAAAAIAHSTB13XeYTq8gAAALgBAAATAAAAW0NvbnRlbnRfVHlwZXNdLnhtbH2Qy07DMBBF9/0Ka7aodmCBEIrTBY8lsCgfYNmTxKo9tjxuSP8epYUiIcr6Ps6daTdzDGLCwj6RhmvZgECyyXkaNLxvn9d3ILgaciYkQg0HZNh0q3Z7yMhijoFYw1hrvleK7YjRsEwZaY6hTyWayjKVQWVjd2ZAddM0t8omqkh1XZcO6FZCtI/Ym32o4mmuSKctBQODeDh5F5wGk3Pw1lSfSE3kfoHWXxBZMBw9PPrMV3MMoC5BFvEy4yf6OmEp3qF4M6W+mIga1EcqTrlk9xGpyv+b/lib+t5bPOeXtlySRWZPQwzyrETj6fuKVh0f330CUEsDBBQAAAAIAHSTB10gG4bqtgAAAC4BAAALAAAAX3JlbHMvLnJlbHONz7FOxDAQBNA+X7Ha/uIcBUIozjUnpGtR+ADL3iQW9q7l9UHu72koOERBOxq90YynPSf4oKpR2OKxHxCIvYTIq8W3+eXwhKDNcXBJmCzeSPE0deMrJdeisG6xKOw5sVrcWivPxqjfKDvtpRDvOS1Ss2vaS11Ncf7drWQehuHR1J8GTh3AHQuXYLFewhFhvhX6Dy/LEj2dxV8zcftj5VcDYXZ1pWbxU2ow4Tvu95zQTN1o7m5OX1BLAwQUAAAACAB0kwdd3m/A9o4AAACvAAAAEQAAAHdvcmQvZG9jdW1lbnQueG1sRc0xDgIhEEDR3lMQepfVwhgCu50n0AMg4ErCzBAGZff2xsJY/bzqm3mFLN6xciK08jCMUkT0FBIuVt6ul/1ZCm4Og8uE0cotspynnek6kH9BxCZWyMi6W/lsrWil2D8jOB6oRFwhP6iCazxQXVSnGkolH5kTLpDVcRxPClxCOZmu7xS2b4uajPpR/VfTB1BLAQIUABQAAAAIAHSTB13XeYTq8gAAALgBAAATAAAAAAAAAAAAAACAAQAAAABbQ29udGVudF9UeXBlc10ueG1sUEsBAhQAFAAAAAgAdJMHXSAbhuq2AAAALgEAAAsAAAAAAAAAAAAAAIABIwEAAF9yZWxzLy5yZWxzUEsBAhQAFAAAAAgAdJMHXd5vwPaOAAAArwAAABEAAAAAAAAAAAAAAIABAgIAAHdvcmQvZG9jdW1lbnQueG1sUEsFBgAAAAADAAMAuQAAAL8CAAAAAA==';

const OFFICE_KIND_META: Record<NewOfficeFileKind, NewOfficeFileMeta> = {
  xlsx: {
    extension: '.xlsx',
    defaultName: 'Workbook.xlsx',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    editorKind: 'spreadsheet',
    label: 'Excel workbook'
  },
  docx: {
    extension: '.docx',
    defaultName: 'Document.docx',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    editorKind: 'richedit',
    label: 'Word document'
  }
};

export function getNewOfficeFileMeta(kind: NewOfficeFileKind): NewOfficeFileMeta {
  return OFFICE_KIND_META[kind];
}

export function buildNewFileMenuItems() {
  return buildNewFileMenuItemsFromRegistry();
}

export function normalizeStarterFileName(name: string, starter: NewFileStarter): string {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('File name is required.');
  if (trimmed.includes('/') || trimmed.includes('\\')) {
    throw new Error('File name cannot contain path separators.');
  }
  // Allow bare `.env` as a complete basename for env starters.
  if (starter.extension === '.env' && trimmed.toLowerCase() === '.env') {
    return trimmed;
  }
  return trimmed.toLowerCase().endsWith(starter.extension)
    ? trimmed
    : `${trimmed}${starter.extension}`;
}

export function normalizeOfficeFileName(name: string, kind: NewOfficeFileKind): string {
  const starter = getNewFileStarter(kind);
  if (!starter) throw new Error('Unknown file type.');
  return normalizeStarterFileName(name, starter);
}

export function blankOfficeFile(kind: NewOfficeFileKind, fileName: string): File {
  const meta = OFFICE_KIND_META[kind];
  const bytes = Uint8Array.from(atob(kind === 'xlsx' ? BLANK_XLSX_BASE64 : BLANK_DOCX_BASE64), (c) =>
    c.charCodeAt(0)
  );
  return new File([bytes], fileName, { type: meta.mimeType });
}

function blankStarterFile(starter: NewFileStarter, fileName: string): File {
  if (starter.id === 'xlsx' || starter.id === 'docx') {
    return blankOfficeFile(starter.id, fileName);
  }
  return new File([starter.blankContent], fileName, { type: starter.mimeType });
}

function openCreatedFile(options: {
  starter: NewFileStarter;
  mountId: string;
  relativePath: string;
  allowOfficeEdit: boolean;
  onPreviewInstead?: (state: DocumentPreviewState) => void;
  navigate?: NavigateFn;
}): void {
  const { starter, mountId, relativePath, allowOfficeEdit, navigate } = options;

  if (starter.openSurface === 'spreadsheet' || starter.openSurface === 'richedit') {
    const editorKind: DocumentEditorKind =
      starter.openSurface === 'spreadsheet' ? 'spreadsheet' : 'richedit';
    if (!allowOfficeEdit) {
      showToast('File created. Opened view-only — use a tablet or larger screen to edit.', 'info', 4000);
    }
    openDocumentEditorSession({
      kind: editorKind,
      mountId,
      path: relativePath,
      accessMode: allowOfficeEdit ? 'edit' : 'view',
      preferredMode: 'inAppTab',
      navigate
    });
    return;
  }

  if (
    starter.openSurface === 'monaco' ||
    starter.openSurface === 'code' ||
    starter.openSurface === 'markdown' ||
    starter.openSurface === 'drawio'
  ) {
    openEditorBySurface({
      surface: starter.openSurface,
      mountId,
      path: relativePath,
      preferredMode: 'inAppTab',
      navigate
    });
  }
}

export async function createAndOpenNewStarterFile(options: {
  starterId: string;
  fileName: string;
  fileManagerRef: RefObject<FileManagerRef | null>;
  routes: readonly MountRoute[];
  allowOfficeEdit: boolean;
  onPreviewInstead?: (state: DocumentPreviewState) => void;
  navigate?: NavigateFn;
}): Promise<void> {
  const starter = getNewFileStarter(options.starterId);
  if (!starter) {
    showToast('Unknown file type.', 'error', 3000);
    return;
  }
  if (starter.stub) {
    showToast(`${starter.label} support is coming soon.`, 'info', 3000);
    return;
  }

  const { fileManagerRef, routes, allowOfficeEdit, onPreviewInstead, navigate } = options;
  const instance = fileManagerRef.current?.instance() as FileManagerInstance | undefined;
  if (!instance) return;

  const currentPath = instance.getCurrentDirectory()?.path ?? '';
  const parsedDir = parseItemPathForRoutes({ path: currentPath, name: '' }, routes);
  if (!parsedDir) {
    showToast('Open a storage location first to create a file.', 'warning', 3000);
    return;
  }
  if (parsedDir.mount.mode !== 'ReadWrite') {
    showToast(`"${parsedDir.mount.key}" is read-only.`, 'warning', 3000);
    return;
  }

  let fileName: string;
  try {
    fileName = normalizeStarterFileName(options.fileName, starter);
  } catch (error) {
    showToast(error instanceof Error ? error.message : 'Invalid file name.', 'error', 3000);
    return;
  }

  const file = blankStarterFile(starter, fileName);
  const form = new FormData();
  form.append('file', file);
  const res = await apiFetch(
    `${mountApiBase(parsedDir.mount.id)}/upload?path=${encodeURIComponent(parsedDir.relative)}`,
    { method: 'POST', body: form }
  );

  if (!res.ok) {
    const message =
      res.status === 409
        ? `A file named "${fileName}" already exists.`
        : await readErrorMessage(res);
    showToast(message, 'error', 4000);
    return;
  }

  await focusZipArchiveAfterCreate(instance, currentPath, fileName, routes);

  const relativePath = parsedDir.relative ? `${parsedDir.relative}/${fileName}` : fileName;
  openCreatedFile({
    starter,
    mountId: parsedDir.mount.id,
    relativePath,
    allowOfficeEdit,
    onPreviewInstead,
    navigate
  });
}

export async function createAndOpenNewOfficeFile(options: {
  kind: NewOfficeFileKind;
  fileName: string;
  fileManagerRef: RefObject<FileManagerRef | null>;
  routes: readonly MountRoute[];
  allowOfficeEdit: boolean;
  onPreviewInstead?: (state: DocumentPreviewState) => void;
}): Promise<void> {
  return createAndOpenNewStarterFile({
    starterId: options.kind,
    fileName: options.fileName,
    fileManagerRef: options.fileManagerRef,
    routes: options.routes,
    allowOfficeEdit: options.allowOfficeEdit,
    onPreviewInstead: options.onPreviewInstead
  });
}

/** Resolve virtual path for tests / share helpers. */
export function resolveNewOfficeVirtualPath(
  currentDirectoryPath: string,
  fileName: string,
  routes: readonly MountRoute[]
): string | null {
  const parsedDir = parseItemPathForRoutes({ path: currentDirectoryPath, name: '' }, routes);
  if (!parsedDir) return null;
  const relative = parsedDir.relative ? `${parsedDir.relative}/${fileName}` : fileName;
  return toVirtualPath(parsedDir.mount.key, relative);
}
